import { Injectable } from '@angular/core';

import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  IServerSideGetRowsParams,
  RowHeightParams,
  ValueFormatterParams
} from 'ag-grid-enterprise';

import {
  SuccessNotificationComponent
} from '@components/notifications/success-notification/success-notification.component';
import { ErrorNotificationComponent } from '@components/notifications/error-notification/error-notification.component';
import { LinkRendererComponent } from '@components/table/cell-renderers/link-renderer/link-renderer.component';
import { AuthService } from '@services/auth.service';
import { ListService } from '@services/list.service';
import { TimeService } from '@services/time.service';
import { UtilitiesService } from '@services/utilities.service';
import { TABLE_TYPES } from '@configs/table';
import { ERROR_NOTIFICATION_CONFIG, NOTIFICATION_TYPES, SUCCESS_NOTIFICATION_CONFIG } from '@configs/notification';
import { FILTER_TYPES } from '@configs/search-box';
import { NOTIFICATION_RECIPIENTS, NOTIFICATION_STATES, NOTIFICATION_STATUSES } from '@configs/checkbox';
import { TableConfig } from '@models/table';
import { NotificationAuthor, NotificationEventFormValue } from '@models/notification';
import { SearchBoxConfig, SearchBoxValue } from '@models/search-box';
import { QueryParams } from '@models/api';
import { SearchEmailTemplate } from '@models/email';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  constructor(
    private service: MatSnackBar,
    private authService: AuthService,
    private listService: ListService,
    private timeService: TimeService,
    private utilitiesService: UtilitiesService
  ) { }

  public getNotificationTableConfig(type: NOTIFICATION_TYPES): TableConfig {
    return {
      colDefs: this.filterNotificationTableColumns(type, [
        {
          headerName: 'status',
          field: 'id',
          valueFormatter: (params: ValueFormatterParams) => {
            const { finished, is_error, is_pending } = params.data;

            if (is_pending === '1') {
              return 'pending';
            }

            if (is_error === '1') {
              return 'error';
            }

            return finished ? 'completed' : 'scheduled';
          },
          cellClass: (params: CellClassParams) => this.getNotificationCellClass(params)
        },
        {
          headerName: 'Order',
          field: 'epr_id',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              static: '/order-edit',
              dynamic: 'order_id'
            }
          },
          minWidth: 100
        },
        {
          headerName: 'Created By',
          field: 'created_by'
        },
        {
          headerName: 'Subject',
          field: 'subject',
          autoHeight: true,
          minWidth: 300
        },
        {
          headerName: 'Recipients',
          groupId: 'recipients',
          children: [
            {
              headerName: 'From',
              field: 'from',
              wrapText: true
            },
            {
              headerName: 'To',
              field: 'to',
              wrapText: true,
              columnGroupShow: 'open'
            },
            {
              headerName: 'CC',
              field: 'cc',
              cellRenderer: this.recipientsCellRenderer,
              minWidth: 300,
              wrapText: true,
              columnGroupShow: 'open'
            },
            {
              headerName: 'BCC',
              field: 'bcc',
              cellRenderer: this.recipientsCellRenderer,
              minWidth: 300,
              wrapText: true,
              columnGroupShow: 'open'
            }
          ],
          openByDefault: true
        },
        {
          headerName: 'Dates',
          children: [
            {
              headerName: 'Created',
              field: 'created',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampDateTimeFormatter(params)
            },
            {
              headerName: 'Scheduled',
              field: 'scheduled',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampDateTimeFormatter(params),
              columnGroupShow: 'open'
            },
            {
              headerName: 'Finished',
              field: 'finished',
              valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampDateTimeFormatter(params),
              columnGroupShow: 'open'
            }
          ]
        }
      ]),
      getRowHeight: (params: RowHeightParams): number | undefined | null => {
        if (!params.data) { return null; }
        const {cc, bcc} = params.data,
              ccLineCounter: number = cc ? cc.split(/;/).length : 1,
              bccLineCounter: number = bcc ? bcc.split(/;/).length : 1,
              lineCounter: number = ccLineCounter >= bccLineCounter ? ccLineCounter : bccLineCounter;
        return lineCounter > 2 ? 20 * lineCounter : 48;
      }
    };
  }

  public getEventListTableConfig(): TableConfig {
    return {
      colDefs: this.authService.filterTableColumns(TABLE_TYPES.NOTIFICATION, [
        {
          headerName: 'ID',
          field: 'id',
          minWidth: 60,
          maxWidth: 60
        },
        {
          headerName: 'Name',
          field: 'name'
        },
        {
          headerName: 'Key',
          field: 'eventkey'
        },
        {
          headerName: 'Email Template',
          field: 'email_name',
          valueFormatter: this.utilitiesService.textFormatter,
        },
        {
          headerName: 'Status',
          field: 'active',
          valueFormatter: this.utilitiesService.activeInactiveFormatter,
          cellClass: (params: CellClassParams) => `eos-table-cell-${params.value === '1' ? 'success' : 'danger'}`
        }
      ])
    };
  }

  public getNotificationFilesListTableConfig(): TableConfig {
    return {
      colDefs: [
        {
          headerName: 'Name',
          field: 'name',
          cellRenderer: LinkRendererComponent,
          cellRendererParams: {
            customParams: {
              takeCurrentUrl: true,
              needEmitEvent: true
            }
          },
          minWidth: 250
        },
        {
          headerName: 'Created By',
          field: 'created_by',
          minWidth: 100
        },
        {
          headerName: 'Created',
          field: 'created',
          valueFormatter: (params: ValueFormatterParams) => this.timeService.timestampFormatter(params),
          minWidth: 100
        },
        {
          headerName: 'Template File',
          field: 'is_template_file',
          valueFormatter: (params: ValueFormatterParams) => this.utilitiesService.yesNoFormatter(params)
        }
      ]
    };
  }

  public getNotificationListSearchConfig(): SearchBoxConfig {
    return {
      searchSection: {
        search: true
      },
      filterSection: this.authService.filterSearchBoxConfig(FILTER_TYPES.NOTIFICATION, {
        notificationStatuses: true,
        notificationStatusItems: NOTIFICATION_STATUSES,
        email: true,
        notificationRecipients: true,
        notificationRecipientItems: NOTIFICATION_RECIPIENTS,
        date: true,
        notificationStates: true,
        notificationStateItems: NOTIFICATION_STATES,
        emailTemplates: true,
        notificationAuthors: true
      })
    };
  }

  public prepareNotificationListQueryParams(value: SearchBoxValue, params: IServerSideGetRowsParams): QueryParams {
    const {
        notificationStatuses,
        notificationRecipients,
        date,
        notificationStates,
        emailTemplates,
        notificationAuthors
      } = value,
      {complete, scheduled, error, pending} = notificationStatuses,
      {
        from: email_address_from,
        to: email_address_to,
        cc: email_address_cc,
        bcc: email_address_bcc
      } = notificationRecipients,
      {
        created: date_range_created,
        scheduled: date_range_scheduled,
        finished: date_range_finished
      } = notificationStates,
      {start, end} = date;

    return {
      ...this.listService.prepareBaseQueryParams(value, params),
      complete,
      scheduled,
      error,
      pending,
      email_text: value.email,
      email_address_from,
      email_address_to,
      email_address_cc,
      email_address_bcc,
      date_range_created,
      date_range_scheduled,
      date_range_finished,
      from: start ? this.timeService.formatDateTime(this.timeService.addStartDay(start)) : null,
      to: end ? this.timeService.formatDateTime(this.timeService.addEndDay(end)) : null,
      email_template_id: emailTemplates && emailTemplates.length
        ? emailTemplates.map((template: SearchEmailTemplate) => template.id)
        : null,
      created_by: notificationAuthors && notificationAuthors.length
        ? notificationAuthors.map((author: NotificationAuthor) => author.id)
        : null
    };
  }

  public prepareEventFormValue(value: NotificationEventFormValue): NotificationEventFormValue {
    return {
      ...value,
      settings: [{
        id: 0,
        cc: value.cc,
        bcc: value.bcc
      }]
    };
  }

  public openSuccessNotification(message: string, config?: MatSnackBarConfig): void {
    config ??= {
      ...SUCCESS_NOTIFICATION_CONFIG,
      data: {message}
    };

    this.service.openFromComponent(SuccessNotificationComponent, config);
  }

  public openErrorNotification(message: string, config?: MatSnackBarConfig): void {
    config ??= {
      ...ERROR_NOTIFICATION_CONFIG,
      data: {message}
    };

    this.service.openFromComponent(ErrorNotificationComponent, config);
  }

  private getNotificationCellClass(params: CellClassParams): string {
    const { finished, is_error, is_pending } = params.data,
          clazz: string = 'eos-table-cell-';

    if (is_pending === '1') {
      return `${clazz}default`;
    }

    if (is_error === '1') {
      return `${clazz}danger`;
    }

    return `${clazz}${finished ? 'success' : 'info'}`;
  }

  private recipientsCellRenderer(params: ICellRendererParams): string {
    return params.value ? params.value.replaceAll(/;/g, '<br>') : '---';
  }

  // TODO: Right now filter only "order" column for company notification
  private filterNotificationTableColumns(
    type: NOTIFICATION_TYPES,
    columnDefs: Array<ColDef | ColGroupDef>
  ): Array<ColDef | ColGroupDef> {
    return columnDefs.filter((column: ColDef | ColGroupDef) => {
      return !(type === NOTIFICATION_TYPES.COMPANY && (<ColDef>column).field === 'epr_id');
    });
  }
}
